/**
 * 통화 포맷 공통 유틸
 * - KRW: ₩1,234,567 (소수점 없음)
 * - USD: $1,234.56
 * - 정책: 한국어 UI는 KRW, 그 외 공식 언어 UI는 USD
 */

import type { GetOrderDetailResponseDto, OrderItemDetailDto, OrderListItemDto } from '../types/api/order';
import { normalizeLocaleTag } from '@/lib/i18nLocale';

export type SupportedCurrencyCode = 'KRW' | 'USD';

const CURRENCY_LOCALE_MAP: Record<SupportedCurrencyCode, string> = {
    KRW: 'ko-KR',
    USD: 'en-US',
};

export function normalizeSupportedCurrency(currency: string | undefined | null): SupportedCurrencyCode {
    return String(currency ?? '').toUpperCase() === 'USD' ? 'USD' : 'KRW';
}

export function displayCurrencyForLocale(locale: string | undefined | null): SupportedCurrencyCode {
    return normalizeLocaleTag(locale) === 'ko' ? 'KRW' : 'USD';
}

/**
 * 통화에 맞는 기호와 형식으로 금액을 포맷합니다.
 * - null/undefined → '-'
 */
export function formatCurrency(
    amount: number | null | undefined,
    currency: string | undefined | null,
    opts?: { nullDisplay?: string; locale?: string }
): string {
    const nullDisplay = opts?.nullDisplay ?? '-';
    if (amount == null) return nullDisplay;

    const c = normalizeSupportedCurrency(currency);
    const locale = opts?.locale ?? CURRENCY_LOCALE_MAP[c];

    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: c,
            maximumFractionDigits: c === 'KRW' ? 0 : 2,
            minimumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${amount.toLocaleString(locale)} ${c}`;
    }
}

export function formatCurrencyForLocale(
    amount: number | null | undefined,
    locale: string | undefined | null,
    opts?: { nullDisplay?: string }
): string {
    return formatCurrency(amount, displayCurrencyForLocale(locale), {
        nullDisplay: opts?.nullDisplay,
    });
}

/**
 * 기호(₩, $ 등) 없이 금액만 포맷 — 별도 열에 통화코드가 있을 때 사용
 */
export function formatCurrencyAmountOnly(
    amount: number | null | undefined,
    currency: string | undefined | null,
    opts?: { nullDisplay?: string }
): string {
    const nullDisplay = opts?.nullDisplay ?? '-';
    if (amount == null) return nullDisplay;

    const c = normalizeSupportedCurrency(currency);
    const locale = CURRENCY_LOCALE_MAP[c];
    try {
        return new Intl.NumberFormat(locale, {
            maximumFractionDigits: c === 'KRW' ? 0 : 2,
            minimumFractionDigits: 0,
        }).format(amount);
    } catch {
        return amount.toLocaleString(locale);
    }
}

/**
 * 0 이하 값은 '-'로 표시 (판매단위 테이블용)
 */
export function formatCurrencyOrDash(
    amount: number | null | undefined,
    currency: string | undefined | null
): string {
    if (amount == null || amount <= 0) return '-';
    return formatCurrency(amount, currency);
}

/**
 * 텍스트(예: "연간 기기구독(USD)", "Basic / $199", "100달러")에서 통화코드를 추출합니다.
 */
export function detectCurrencyFromText(text: string | null | undefined): string | undefined {
    if (!text) return undefined;
    const u = text.toUpperCase();
    const iso = u.match(/\b(KRW|USD)\b/);
    if (iso) return iso[1];
    // 한글·기호 (ISO 없을 때)
    if (/달러|미국달러|US달러|USD화/.test(text)) return 'USD';
    if (/₩|원화|\(원\)/.test(text)) return 'KRW';
    // 금액에 $ 사용 (예: $1,200, $ 99)
    if (/\$\s*[\d]/.test(text) || /[\d.,]+\s*\$/.test(text)) return 'USD';
    return undefined;
}

/** 백엔드가 camelCase·스네이크 혼용으로 주는 통화 필드 */
const CURRENCY_FIELD_KEYS = [
    'currency',
    'currencyCode',
    'currency_code',
    'priceCurrency',
    'price_currency',
    'totalCurrency',
    'total_currency',
    'settlementCurrency',
    'settlement_currency',
    'amountCurrency',
    'amount_currency',
    'orderCurrency',
    'order_currency',
    'paymentCurrency',
    'payment_currency',
    'invoiceCurrency',
    'invoice_currency',
    'moneyCurrency',
    'money_currency',
    'displayCurrency',
    'display_currency',
    'baseCurrency',
    'base_currency',
] as const;

/**
 * 객체에 명시된 ISO 통화 코드만 추출 (주문/품목 루트·스네이크 케이스 대응)
 */
export function pickCurrencyCodeFromRecord(rec: Record<string, unknown> | null | undefined): string | undefined {
    if (!rec) return undefined;
    for (const key of CURRENCY_FIELD_KEYS) {
        const v = rec[key];
        if (v == null) continue;
        const s = String(v).trim();
        if (!s) continue;
        const up = s.toUpperCase();
        if (/^(KRW|USD)$/.test(up)) return up;
    }
    return undefined;
}

/**
 * OpenAPI는 userName·description 등을 object로 표기 — 문자열 blob·통화 스캔용으로 한 줄 텍스트로 평탄화
 */
function flattenApiTextField(v: unknown, depth = 0): string {
    if (v == null || depth > 6) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'object' && v !== null) {
        const o = v as Record<string, unknown>;
        for (const k of ['title', 'value', 'str', 'text', 'content', 'name', 'label', 'nickname']) {
            const x = o[k];
            if (typeof x === 'string' && x.trim()) return x.trim();
        }
        const d = o.data;
        if (typeof d === 'string' && d.trim()) return d.trim();
        if (d != null && typeof d === 'object') {
            const inner = flattenApiTextField(d, depth + 1);
            if (inner) return inner;
        }
        for (const val of Object.values(o)) {
            const inner = flattenApiTextField(val, depth + 1);
            if (inner) return inner;
        }
    }
    return '';
}

/** 응답 객체 전체를 순회하며 ISO 통화 또는 $·USD 등 힌트가 있는 문자열 탐색 (목록 행에 currency 키가 없을 때) */
function deepScanCurrencyInApiPayload(value: unknown, depth = 0): string | undefined {
    if (value == null || depth > 12) return undefined;
    if (typeof value === 'string') {
        const t = value.trim();
        if (!t) return undefined;
        const exact = t.match(/^(KRW|USD)$/i);
        if (exact) return exact[1].toUpperCase();
        return detectCurrencyFromText(t);
    }
    if (typeof value === 'number' || typeof value === 'boolean') return undefined;
    if (Array.isArray(value)) {
        for (const el of value) {
            const hit = deepScanCurrencyInApiPayload(el, depth + 1);
            if (hit) return hit;
        }
        return undefined;
    }
    if (typeof value === 'object') {
        for (const v of Object.values(value as Record<string, unknown>)) {
            const hit = deepScanCurrencyInApiPayload(v, depth + 1);
            if (hit) return hit;
        }
    }
    return undefined;
}

/** GET /order/{id}/detail — 품목 판매단위명·비고 등에서 통화 추정 (목록만으로는 USD가 안 잡히는 경우 대비) */
function detectCurrencyFromOrderDetailBlob(detail: GetOrderDetailResponseDto): string | undefined {
    const root = detail as unknown as Record<string, unknown>;
    const fromRoot = pickCurrencyCodeFromRecord(root);
    if (fromRoot) return fromRoot;

    const deep = deepScanCurrencyInApiPayload(detail);
    if (deep) return deep;

    const parts: string[] = [];
    const dDesc = flattenApiTextField(detail.description);
    if (dDesc) parts.push(dDesc);
    const dDealer = flattenApiTextField(detail.dealerOrganizationTitle);
    if (dDealer) parts.push(dDealer);
    const dProd = flattenApiTextField(detail.productTitle);
    if (dProd) parts.push(dProd);
    const dClass = flattenApiTextField(detail.classTitle);
    if (dClass) parts.push(dClass);

    for (const it of detail.items ?? []) {
        const row = it as OrderItemDetailDto & Record<string, unknown>;
        const fromItem = pickCurrencyCodeFromRecord(row);
        if (fromItem) return fromItem;
        const deepItem = deepScanCurrencyInApiPayload(row);
        if (deepItem) return deepItem;
        const ppn = flattenApiTextField(it.productPriceName);
        if (ppn) parts.push(ppn);
        const pn = flattenApiTextField(it.productName);
        if (pn) parts.push(pn);
    }
    const blob = parts.join(' ');
    return detectCurrencyFromText(blob);
}

/**
 * 주문 목록 한 행 — 선택 시 상세(detail)를 넘기면 품목 기준으로 통화를 우선한다.
 */
export function detectCurrencyFromOrderListRow(row: OrderListItemDto, detail?: GetOrderDetailResponseDto | null): string {
    if (detail) {
        const fromDetail = detectCurrencyFromOrderDetailBlob(detail);
        if (fromDetail) return fromDetail;
    }
    const ext = row as OrderListItemDto & Record<string, unknown>;
    const fromList = pickCurrencyCodeFromRecord(ext);
    if (fromList) return fromList;
    for (const [k, v] of Object.entries(ext)) {
        if (!/currency/i.test(k) || typeof v !== 'string') continue;
        const up = v.trim().toUpperCase();
        if (/^(KRW|USD)$/.test(up)) return up;
    }
    const deepRow = deepScanCurrencyInApiPayload(ext);
    if (deepRow) return deepRow;

    const nameParts = (Array.isArray(row.productNames) ? row.productNames : []).map((s) => String(s).trim()).filter(Boolean);
    const blob = [
        flattenApiTextField(row.productName),
        ...nameParts,
        flattenApiTextField(row.organizationName),
        flattenApiTextField(row.userName),
        typeof row.orderNumber === 'string' ? row.orderNumber.trim() : '',
    ]
        .filter(Boolean)
        .join(' ');
    return detectCurrencyFromText(blob) ?? 'KRW';
}
